import { useMemo } from "react";
import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import { ASSETS_QUERY_KEY } from "../assets/hooks";
import { ASSET_ASSIGNMENTS_QUERY_KEY } from "../asset-assignments/hooks";
import { useDebouncedValue } from "../common/useDebouncedValue";
import { addAssetToUser, lookupUser, searchUsers } from "./api";
import type {
    AddAssetToUserPayload,
    AddAssetToUserResult,
    UserDirectoryEntry,
} from "./types";

export const USER_DIRECTORY_QUERY_KEY = ["admin", "users", "search"] as const;
export const USER_DIRECTORY_LOOKUP_KEY = ["admin", "users", "lookup"] as const;

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

/**
 * Debounced typeahead against the Office user-search proxy. The query is
 * only fired when the trimmed term has at least {@link MIN_QUERY_LENGTH}
 * characters, mirroring the API's own minimum so the round-trip can't be
 * triggered with a single keystroke. Results are cached briefly so quick
 * back-arrow / re-type cycles don't re-fetch.
 */
export function useUserDirectorySearch(query: string): UseQueryResult<
    readonly UserDirectoryEntry[],
    Error
> {
    const client = useApiClient();
    const debounced = useDebouncedValue(query.trim(), DEBOUNCE_MS);

    return useQuery({
        queryKey: [...USER_DIRECTORY_QUERY_KEY, debounced],
        queryFn: () => searchUsers(client, debounced),
        enabled: debounced.length >= MIN_QUERY_LENGTH,
        staleTime: 30_000,
    });
}

/**
 * Resolves a single user id to its directory entry. Used by detail views
 * that show one userId field — `<UserName id={...} />` is the typical
 * caller. Returns `null` (not an error) when the id is empty or HR has no
 * record, so callers can fall back to displaying the raw id without
 * special-casing rejection states.
 *
 * `staleTime: Infinity` is deliberate: a user's display name doesn't
 * change inside a session, and HR is the system of record. Anything
 * shorter would re-fetch on every focus/return-to-tab and add latency to
 * a screen we expect to feel instantaneous after the first hit.
 */
export function useUserDirectoryEntry(
    id: string | null | undefined,
): UseQueryResult<UserDirectoryEntry | null, Error> {
    const client = useApiClient();
    const trimmed = typeof id === "string" ? id.trim() : "";

    return useQuery({
        queryKey: [...USER_DIRECTORY_LOOKUP_KEY, trimmed],
        queryFn: () => lookupUser(client, trimmed),
        enabled: trimmed !== "",
        staleTime: Infinity,
    });
}

export interface UserDisplayNamesResult {
    readonly isLoading: boolean;
    readonly map: ReadonlyMap<string, UserDirectoryEntry | null>;
}

/**
 * Resolves a list of user ids to a map of `id → directory entry | null`.
 * Used by list views that render N rows containing user references; the
 * page computes the unique ids once and passes the resulting map down to
 * the row renderer.
 *
 * Inputs are deduplicated and sorted so the underlying TanStack
 * `useQueries` invocation has stable, deterministic query keys across
 * renders. Each id is fetched as an independent query so the cache can
 * be shared with `useUserDirectoryEntry` (a detail view opened for the
 * same user reuses the list's already-fetched entry).
 *
 * The returned map always has an entry for every input id, valued
 * `null` while pending or when HR returns 404 — callers render the raw
 * id as a fallback in either case.
 */
export function useUserDisplayNames(ids: readonly string[]): UserDisplayNamesResult {
    const client = useApiClient();

    const unique = useMemo(() => {
        const seen = new Set<string>();
        for (const raw of ids) {
            const trimmed = raw.trim();
            if (trimmed === "") continue;
            seen.add(trimmed);
        }
        return Array.from(seen).sort();
    }, [ids]);

    return useQueries({
        queries: unique.map((id) => ({
            queryKey: [...USER_DIRECTORY_LOOKUP_KEY, id],
            queryFn: () => lookupUser(client, id),
            staleTime: Infinity,
        })),
        combine: (results) => {
            const map = new Map<string, UserDirectoryEntry | null>();
            for (let i = 0; i < unique.length; i += 1) {
                const id = unique[i];
                if (id === undefined) continue;
                map.set(id, results[i]?.data ?? null);
            }
            return {
                isLoading: results.some((r) => r.isPending),
                map,
            };
        },
    });
}

export interface AddAssetToUserInput {
    readonly userId: string;
    readonly payload: AddAssetToUserPayload;
}

/**
 * Mutation for the user-anchored "add asset" call. One round-trip
 * creates both the asset and the user-target assignment atomically; the
 * `onSuccess` invalidates every cache that could be showing the new rows
 * — assets list and asset-assignments list. (Workspaces don't change as
 * a side-effect of a user-target assignment.)
 */
export function useAddAssetToUser(): UseMutationResult<
    AddAssetToUserResult,
    Error,
    AddAssetToUserInput
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, payload }) => addAssetToUser(client, userId, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY });
        },
    });
}
